import type { PostCreateSchema } from '@repo/schemas/post/postCreate';
import type { PostMutationOptions, PostPrivate } from '@repo/types/posts';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useCreatePost } from '@repo/api/post/useCreatePost';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { postCreateSchema } from '@repo/schemas/post/postCreate';
import { BookTextIcon, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, ClockFadingIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import EditPostsPublishedAtDialog from '@/components/features/manage/manage-posts/EditPostsPublishedAtDialog';
import { PostAudienceTab } from '@/components/features/manage/manage-posts/PostAudienceTab';
import { PostFormFields } from '@/components/features/manage/manage-posts/PostFormFields';
import { Button, ButtonGroup } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { Form } from '@/components/ui/form';
import { LoadingOverlay, LoadingOverlayLabel, LoadingOverlaySpinner } from '@/components/ui/loading-overlay';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { usePostDialog } from '@/hooks/posts';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';
import { useCredentials } from '@/store/auth-store';

const FORM_DEFAULT_VALUES = {
  name: '',
  content: '',
  active: true,
  global: true,
  attachments: undefined,
  publishedAt: null,
  groups: undefined,
};

/**
 * Props for the CreatePost component.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(bool: boolean) => void} onOpenChange - Callback to change dialog open state.
 * @property {UseMutationResult} mutation - The mutation hook to use for creating posts.
 */
interface CreatePostProps {
  open: boolean;
  onOpenChange: (bool: boolean) => void;
  defaultValues?: Partial<PostPrivate>;
  postOptions?: PostMutationOptions;
}

export default function CreatePost(createPostProps: CreatePostProps) {
  const { open, onOpenChange, defaultValues: _defaultValues, postOptions } = createPostProps;

  const { groups: defaultGroups, ...defaultValues } = _defaultValues || {};
  const [openPublishDialog, setOpenPublishDialog] = useState<boolean>(false);
  const { name, surname, avatarURL } = useCredentials();

  const form = useForm<PostCreateSchema>({
    defaultValues: {
      ...FORM_DEFAULT_VALUES,
      ...defaultValues,
    },
    mode: 'onSubmit',
    resolver: standardSchemaResolver(postCreateSchema),
  });

  const globalValue = useWatch({ control: form.control, name: 'global' });

  const {
    tabs,
    setTabs,
    isMainTab,
    audience,
    searchGroupValue,
    setSearchGroupValue,
    selectedGroups,
    setSelectedGroups,
    onRemoveGroup,
    files,
    onAddFiles,
    onRemoveFile,
    resetDialog,
    groups,
    groupsIsEmpty,
    groupsNoResults,
    groupsErrorMessage,
    getGroupsInfinite,
  } = usePostDialog(globalValue);

  const { mutateAsync, isPending } = useCreatePost(postOptions);

  const onClose = () => {
    onOpenChange(false);
    resetDialog();
    form.reset();
  };

  const onSubmit = (data: PostCreateSchema) => {
    const attachments = files.map(({ file }) => file);
    const groups = selectedGroups.map(({ id }) => id);

    const isGlobal: boolean = data.global;
    mutateAsync({ ...data, attachments: attachments, groups: isGlobal ? undefined : groups, global: isGlobal })
      .then(() => {
        onClose();
      })
      .catch((error) => {
        toasterMessageHandler(baseErrorMessages, error.message);
      });
  };

  const onSubmitNow = (data: PostCreateSchema) => {
    const attachments = files.map(({ file }) => file);
    const groups = selectedGroups.map(({ id }) => id);

    const isGlobal: boolean = data.global;

    mutateAsync({ ...data, attachments: attachments, publishedAt: null, groups: isGlobal ? undefined : groups, global: isGlobal })
      .then(() => {
        onClose();
      })
      .catch((error) => {
        toasterMessageHandler(baseErrorMessages, error.message);
      });
  };

  const onSubmitPublishedAt = (data: Pick<PostCreateSchema, 'publishedAt'>) => {
    form.setValue('publishedAt', data.publishedAt);
    form.handleSubmit(onSubmit)();
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    if (defaultGroups && defaultGroups.length > 0) {
      setSelectedGroups(defaultGroups);
      form.setValue('global', false, { shouldDirty: false });
    }
    form.reset({
      ...FORM_DEFAULT_VALUES,
      ...defaultValues,
    });
  }, [defaultGroups, defaultValues, form, open, setSelectedGroups]);

  return (
    <>
      <EditPostsPublishedAtDialog open={openPublishDialog} onOpenChange={setOpenPublishDialog} onSubmit={onSubmitPublishedAt} />
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={'sm:h-full sm:max-h-206 sm:max-w-240'} isClosable={isMainTab} onCloseAutoFocus={onClose} onEscapeKeyDown={onClose}>
          <Form {...form}>
            <DialogBody>
              <LoadingOverlay isLoading={isPending}>
                <LoadingOverlaySpinner />
                <LoadingOverlayLabel>Trwa tworzenie posta...</LoadingOverlayLabel>
              </LoadingOverlay>
              <DialogHeader className={'border-border-primary bg-card items-center gap-2 border-b'}>
                {isMainTab ? (
                  <>
                    <FeaturedIcon size={'lg'}>
                      <BookTextIcon />
                    </FeaturedIcon>
                    <div className={'flex min-w-0 grow flex-col gap-0.5'}>
                      <DialogTitle>Utwórz Post</DialogTitle>
                      <DialogDescription className={'text-muted-foreground text-xs'}>Stwórz opis dla posta</DialogDescription>
                    </div>
                  </>
                ) : (
                  <>
                    <Button variant={'outline'} onClick={() => setTabs('main')}>
                      <ChevronLeftIcon />
                    </Button>
                    <div className={'flex min-w-0 grow flex-col gap-0.5'}>
                      <DialogTitle>Odbiorcy</DialogTitle>
                      <DialogDescription className={'text-muted-foreground text-xs'}>Wybierz grupy odbiorców dla posta</DialogDescription>
                    </div>
                  </>
                )}
              </DialogHeader>
              <Tabs value={tabs} onValueChange={(tab) => setTabs(tab as 'main' | 'audience')} className={'flex grow flex-col gap-3 overflow-auto'}>
                <TabsContent value={'main'} className={'flex grow flex-col gap-3 p-4'}>
                  <form className={'flex grow flex-col gap-3'} onSubmit={form.handleSubmit(onSubmit)} noValidate>
                    <PostFormFields
                      files={files}
                      onRemoveFile={onRemoveFile}
                      onAddFiles={onAddFiles}
                      author={{ name, surname, avatarURL }}
                      audience={audience}
                      onOpenAudience={() => setTabs('audience')}
                      selectedGroups={selectedGroups}
                    />
                  </form>
                </TabsContent>
                <TabsContent value={'audience'} className={'flex grow flex-col gap-3 p-4'}>
                  <PostAudienceTab
                    audience={audience}
                    onAudienceChange={(value) => {
                      form.setValue('global', value === 'public', {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      });
                    }}
                    searchGroupValue={searchGroupValue}
                    setSearchGroupValue={setSearchGroupValue}
                    selectedGroups={selectedGroups}
                    setSelectedGroups={setSelectedGroups}
                    onRemoveGroup={onRemoveGroup}
                    groups={groups}
                    groupsIsEmpty={groupsIsEmpty}
                    groupsNoResults={groupsNoResults}
                    groupsErrorMessage={groupsErrorMessage}
                    getGroupsInfinite={getGroupsInfinite}
                  />
                </TabsContent>
              </Tabs>
            </DialogBody>
            {isMainTab ? (
              <DialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
                <Button onClick={onClose} type={'button'} variant={'tertiary'}>
                  Anuluj
                </Button>
                <ButtonGroup>
                  <Button variant="primary" disabled={isPending} onClick={form.handleSubmit(onSubmitNow)} type={'submit'}>
                    <LoadingSwap isLoading={isPending} className="flex items-center gap-2">
                      <CheckCircle2Icon />
                      Opublikuj
                    </LoadingSwap>
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="primary">
                        <ChevronDownIcon className="transition-all group-aria-expanded/button:rotate-180" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align={'end'} className="w-full max-w-48">
                      <DropdownMenuLabel>Opublikuj post</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuGroup>
                        <DropdownMenuItem onClick={form.handleSubmit(onSubmitNow)}>
                          <CheckCircle2Icon />
                          Opublikuj teraz
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setOpenPublishDialog(true)}>
                          <ClockFadingIcon />
                          Zaplanuj publikację
                        </DropdownMenuItem>
                      </DropdownMenuGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </ButtonGroup>
              </DialogFooter>
            ) : (
              <DialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
                <Button onClick={() => setTabs('main')} type={'button'} variant={'tertiary'}>
                  Zapisz
                </Button>
              </DialogFooter>
            )}
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}
